import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTutorial } from "@/context/TutorialContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { MapTrifold, Storefront, Gavel, TrendUp, SignOut, User, Target, BellRinging, Bank, Rss, Scales, Star, Calculator, Buildings, Question, Compass, ChartLine, Gear } from "@phosphor-icons/react";
import PwaInstallButton from "@/components/PwaInstallButton";

const navItem = ({ isActive }) =>
  `px-3 py-2 text-sm font-medium tracking-wide transition-colors ${
    isActive ? "text-lime-400 bg-white/5" : "text-zinc-400 hover:text-zinc-100 hover:bg-white/5"
  }`;

export default function AppHeader() {
  const { user, logout } = useAuth();
  const { start: startTour } = useTutorial();
  const navigate = useNavigate();

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 bg-zinc-950 border-b border-white/10"
    >
      <div className="flex items-center h-16 px-4">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-3 mr-6 group"
        >
          <img
            src="/brand/immoscope-icon.png"
            alt="ImmoScope"
            className="h-14 w-14 object-contain transition-transform group-hover:scale-105"
            data-testid="header-logo-img"
          />
          <div className="hidden sm:flex items-baseline">
            <span className="font-display font-black text-xl tracking-[0.18em] text-lime-400">IMMO</span>
            <span className="font-display font-black text-xl tracking-[0.18em] text-zinc-50">SCOPE</span>
          </div>
          <span className="hidden lg:inline text-[10px] font-mono-data text-zinc-500 border border-white/10 px-1.5 py-0.5 ml-1">
            v0.2
          </span>
        </Link>

        {user && (
          <nav className="flex items-center gap-0 border-l border-white/10 pl-4">
            <NavLink to="/dashboard" className={navItem} data-testid="nav-dashboard">
              <span className="flex items-center gap-1.5"><MapTrifold size={14} weight="bold" />Mappa</span>
            </NavLink>
            <NavLink to="/inserzioni" className={navItem} data-testid="nav-inserzioni">
              <span className="flex items-center gap-1.5"><Storefront size={14} weight="bold" />Inserzioni</span>
            </NavLink>
            <NavLink to="/aste" className={navItem} data-testid="nav-aste">
              <span className="flex items-center gap-1.5"><Gavel size={14} weight="bold" />Aste</span>
            </NavLink>
            <NavLink to="/opportunita" className={navItem} data-testid="nav-opportunita">
              <span className="flex items-center gap-1.5"><TrendUp size={14} weight="bold" />Opportunità</span>
            </NavLink>
            <NavLink to="/ricerca" className={navItem} data-testid="nav-ricerca">
              <span className="flex items-center gap-1.5"><Target size={14} weight="bold" />Ricerca ideale</span>
            </NavLink>
            <NavLink to="/aree-salvate" className={navItem} data-testid="nav-aree-salvate">
              <span className="flex items-center gap-1.5"><BellRinging size={14} weight="bold" />Alert</span>
            </NavLink>
            <NavLink to="/agenzie" className={navItem} data-testid="nav-agenzie">
              <span className="flex items-center gap-1.5"><Rss size={14} weight="bold" />Agenzie</span>
            </NavLink>
            <NavLink to="/confronto" className={navItem} data-testid="nav-confronto" data-tour-id="nav-confronto">
              <span className="flex items-center gap-1.5"><Scales size={14} weight="bold" />Confronto</span>
            </NavLink>
            <NavLink to="/preferiti" className={navItem} data-testid="nav-preferiti" data-tour-id="nav-preferiti">
              <span className="flex items-center gap-1.5"><Star size={14} weight="bold" />Preferiti</span>
            </NavLink>
            <NavLink to="/mutuo" className={navItem} data-testid="nav-mutuo">
              <span className="flex items-center gap-1.5"><Calculator size={14} weight="bold" />Mutuo</span>
            </NavLink>
            <NavLink to="/citta" className={navItem} data-testid="nav-citta">
              <span className="flex items-center gap-1.5"><Buildings size={14} weight="bold" />Città</span>
            </NavLink>
            <NavLink to="/npl" className={navItem} data-testid="nav-npl">
              <span className="flex items-center gap-1.5"><Bank size={14} weight="bold" />NPL</span>
            </NavLink>
          </nav>
        )}

        <div className="ml-auto flex items-center gap-3">
          {user ? (
            <>
              <PwaInstallButton />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="w-8 h-8 flex items-center justify-center border border-white/10 text-zinc-400 hover:text-lime-400 hover:border-lime-400/40 transition-colors"
                    data-testid="btn-help-menu"
                    aria-label="Apri guida tutorial"
                  >
                    <Question size={16} weight="bold" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-none bg-zinc-950 border-white/15 text-zinc-100 min-w-[240px]">
                  <DropdownMenuLabel className="font-mono-data text-[10px] tracking-widest text-zinc-500">
                    TUTORIAL INTERATTIVO
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={() => startTour("navigate")}
                    className="rounded-none focus:bg-lime-400/10 focus:text-lime-400 cursor-pointer"
                    data-testid="menu-tour-navigate"
                  >
                    <Compass size={14} weight="bold" className="mr-2 text-lime-400" />
                    <div className="flex flex-col">
                      <span className="font-medium">Come navigare</span>
                      <span className="text-[11px] text-zinc-500">Mappa, ricerca, PDF, preferiti</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => startTour("analyze")}
                    className="rounded-none focus:bg-cyan-400/10 focus:text-cyan-400 cursor-pointer"
                    data-testid="menu-tour-analyze"
                  >
                    <ChartLine size={14} weight="bold" className="mr-2 text-cyan-400" />
                    <div className="flex flex-col">
                      <span className="font-medium">Come analizzare</span>
                      <span className="text-[11px] text-zinc-500">€/m², rendimento, cap rate, COC</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <NavLink
                to="/impostazioni"
                className={({ isActive }) =>
                  `w-8 h-8 flex items-center justify-center border transition-colors ${
                    isActive
                      ? "border-lime-400/60 text-lime-400"
                      : "border-white/10 text-zinc-400 hover:text-lime-400 hover:border-lime-400/40"
                  }`
                }
                data-testid="btn-settings"
                aria-label="Impostazioni"
                title="Impostazioni"
              >
                <Gear size={16} weight="bold" />
              </NavLink>
              <div className="hidden sm:flex items-center gap-2 text-xs text-zinc-400 border-l border-white/10 pl-3">
                <User size={14} weight="bold" />
                <span className="font-mono-data" data-testid="header-user-email">{user.email}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-none"
                onClick={() => { logout(); navigate("/"); }}
                data-testid="btn-logout"
              >
                <SignOut size={14} weight="bold" />
                <span className="ml-1.5">Esci</span>
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm" className="rounded-none text-zinc-300 hover:bg-white/5" data-testid="btn-login-header">
                  Accedi
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="rounded-none bg-lime-400 text-lime-950 hover:bg-lime-500 font-semibold" data-testid="btn-register-header">
                  Registrati →
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
